import { create } from "zustand";
import constants from "../../../../utils/Constants";
import ChatService from "../../../../front-services/ChatService";
import Analytics from "../../../../utils/Analytics";
import localization from "../../../../assets/localization";
import { isExpandableTemplate } from "./templates/utils/showcase";

// What the guest is actually looking at inside a grouped template — used to
// label item_view events with something the hotel can read ("Suite Deluxe"),
// not just an index.
const describeItem = (template: any, index: number, language?: string) => {
  if (!template) return null;

  // HM galleries carry a key ("rooms_gallery"), not a title — resolve it to
  // the localized label and anchor it to the hotel/offer name, otherwise the
  // event lands with a null title and disappears from the top-content lists.
  if (template.type === "hm_hotel" || template.type === "hm_offer") {
    const gallery = template.galleries?.[index];
    if (!gallery) return null;
    const label = localization.get(language ?? "en")[gallery.key] ?? gallery.key;
    return template.title ? `${template.title} — ${label}` : label;
  }

  const inner = template.type === "quote" ? template.rooms : template.templates;
  const item = inner?.[index];
  return item?.title ?? item?.name ?? item?.video_url ?? null;
};

type SliderState = {
  language: string;
  isVisible: boolean;
  isExpanded: boolean;
  currentTemplate: any | null;
  templates: any[] | null;
  selectedKeyword: any;
  keywords: any[] | null;
  subTemplateItemIndex: number;
  assistantId: string | null;
  threadId?: string | null;
  isMobile?: boolean;

  justShow: () => void;
  closeSlider: () => void;
  reset: () => void;
  setExpanded: (expanded: boolean) => void;
  setExploreOpen: (open: boolean) => void;
  isExploreOpen: boolean;
  setSubTemplateItemIndex: (index: number) => void;
  setCurrentTemplate: (template: any | null, index: number) => void;
  setTemplates: (templates: any[] | null) => void;
  selectKeyword: (keyword: string) => void;
  setKeywords: (keywords: any[]) => void;
  openSlider: (
    threadId: string,
    assistantId: string,
    templates: any,
    isMobile: boolean
  ) => void;
  openFromAnchor: (
    threadId: string | null,
    assistantId: string,
    templates: any,
    isMobile: boolean,
    focusIndex?: number
  ) => void;
  hydrateFromCache: (
    threadId: string | null,
    assistantId: string,
    templatesList: any[][],
    isMobile: boolean
  ) => void;
  setLanguage: (language: string) => void;
  next: () => void;
  previous: () => void;
};

export const useSliderState = create<SliderState>((set, get) => ({
  // Placeholder until Chat pushes the sanitized locale in via setLanguage.
  language: "en",
  isMobile: false,
  isVisible: false,
  isExpanded: false,
  currentTemplate: null,
  animation: {
    pendingTemplate: null,
    pendingIndex: null,
    isAnimating: false,
  },
  templates: null,
  keywords: [],
  assistantId: null,
  threadId: null,
  selectedKeyword: null,
  subTemplateItemIndex: 0,
  isExploreOpen: false,
  justShow: () =>
    set({
      isVisible: true,
    }),
  closeSlider: () =>
    set({ isVisible: false, isExpanded: false, isExploreOpen: false }),
  // Deleting the conversation must wipe the slider too — Esplora is the
  // session's memory, and the session is gone.
  reset: () =>
    set({
      isVisible: false,
      isExpanded: false,
      isExploreOpen: false,
      currentTemplate: null,
      templates: null,
      subTemplateItemIndex: 0,
      selectedKeyword: null,
      keywords: [],
    }),
  setExpanded: (expanded: boolean) => {
    if (get().isExpanded !== expanded) {
      Analytics.track(expanded ? "fullscreen_enter" : "fullscreen_exit", {
        template_type: get().currentTemplate?.type ?? null,
      });
    }
    set({ isExpanded: expanded });
  },
  setExploreOpen: (open: boolean) => {
    if (get().isExploreOpen !== open) {
      Analytics.track(open ? "explore_open" : "explore_close");
    }
    set({ isExploreOpen: open });
  },
  setCurrentTemplate: (template: any | null, index = 0) => {
    let _templates = get().templates || [];
    _templates = _templates.filter((t: any) => t !== template);

    Analytics.track("item_view", {
      template_type: template?.type ?? null,
      item_index: index,
      item_title: describeItem(template, index, get().language),
    });

    // Update state immediately
    set({
      currentTemplate: template,
      subTemplateItemIndex: index,
      templates: [template, ..._templates],
    });
  },
  setLanguage: (language: string) => set({ language }),
  setTemplates: (templates: any[] | null) => set({ templates }),
  selectKeyword: (keyword: string) =>
    set({ selectedKeyword: keyword.toLowerCase(), isVisible: true }),
  setKeywords: (keywords: any[]) => set({ keywords }),
  setSubTemplateItemIndex: (index: number) => {
    const template = get().currentTemplate;
    if (get().subTemplateItemIndex !== index) {
      Analytics.track("item_view", {
        template_type: template?.type ?? null,
        item_index: index,
        item_title: describeItem(template, index, get().language),
      });
    }
    set({ subTemplateItemIndex: index });
  },
  openSlider: (
    threadId,
    assistantId: string,
    templates: any,
    isMobile: boolean
  ) => {
    if (!constants.FEATURE_FLAGS.SLIDER || !templates) return;

    if (typeof templates === "string") {
      try {
        templates = JSON.parse(templates);
      } catch (error) {
        console.error("Failed to parse templates string:", error);
        return;
      }
    }

    if (!Array.isArray(templates)) {
      templates = [templates];
    }

    /*
      const keywords = KeywordHandler.returnOrderedKeywords(
        templates,
        get().keywords
      );
    */
    let groupedTemplates = tagWithKeys(groupTemplates(templates));
    if (!groupedTemplates.length) return;

    // Same content already in the stack (e.g. the bot re-sent the rooms)
    // replaces the old copy instead of piling up a duplicate.
    const newKeys = new Set(groupedTemplates.map((t: any) => t.__key));
    let prevTemplates = (get().templates || []).filter(
      (t: any) => !newKeys.has(t.__key)
    );
    let isVisible = !isMobile;

    if (ChatService.isHM(assistantId)) isVisible = true;

    Analytics.track("templates_shown", {
      template_types: groupedTemplates.map((t: any) => t.type),
    });

    set({
      isVisible: isVisible,
      isExploreOpen: false,
      currentTemplate: groupedTemplates[0],
      templates: [...groupedTemplates, ...prevTemplates],
      selectedKeyword: null,
      subTemplateItemIndex: 0,
      assistantId: assistantId,
      threadId: threadId,
      isMobile: isMobile,
    });
  },
  openFromAnchor: (
    threadId,
    assistantId: string,
    templates: any,
    isMobile: boolean,
    focusIndex: number = 0
  ) => {
    if (!constants.FEATURE_FLAGS.SLIDER || !templates) return;

    const groupedTemplates = tagWithKeys(groupTemplates(templates));
    if (!groupedTemplates.length) return;

    const target =
      groupedTemplates[Math.min(focusIndex, groupedTemplates.length - 1)];

    const state = get();
    const stack = state.templates || [];
    // If this reply's templates are already in the stack, focus the existing
    // copy; otherwise prepend them (a returning visitor reopening an anchor).
    const existing = stack.find((t: any) => t.__key === target.__key);

    let nextStack = stack;
    let current = existing;
    if (!existing) {
      const newKeys = new Set(groupedTemplates.map((t: any) => t.__key));
      nextStack = [
        ...groupedTemplates,
        ...stack.filter((t: any) => !newKeys.has(t.__key)),
      ];
      current = target;
    }

    Analytics.track("anchor_click", {
      template_type: current?.type ?? null,
    });

    set({
      isVisible: true,
      // Opening an anchor while in fullscreen keeps the fullscreen alive if
      // the new content can live there; otherwise drop back to the widget.
      isExpanded: get().isExpanded && isExpandableTemplate(current),
      isExploreOpen: false,
      currentTemplate: current,
      templates: nextStack,
      selectedKeyword: null,
      subTemplateItemIndex: 0,
      assistantId: assistantId,
      threadId: threadId,
      isMobile: isMobile,
    });
  },
  // Rebuilds the Esplora stack from the cached conversation on load — a
  // returning visitor finds everything the concierge showed them, without
  // the slider popping open on its own.
  hydrateFromCache: (
    threadId,
    assistantId: string,
    templatesList: any[][],
    isMobile: boolean
  ) => {
    if (!constants.FEATURE_FLAGS.SLIDER || !templatesList?.length) return;

    let stack: any[] = get().templates || [];
    for (const raw of templatesList) {
      const grouped = tagWithKeys(groupTemplates(raw ?? []));
      if (!grouped.length) continue;
      const keys = new Set(grouped.map((t: any) => t.__key));
      stack = [...grouped, ...stack.filter((t: any) => !keys.has(t.__key))];
    }
    if (!stack.length) return;

    set({
      templates: stack,
      currentTemplate: stack[0],
      subTemplateItemIndex: 0,
      assistantId: assistantId,
      threadId: threadId,
      isMobile: isMobile,
    });
  },
  next: () => {
    const state = get();

    if (!state.templates || state.templates.length === 0) return;

    switch (state.currentTemplate?.type) {
      case "list_views":
        if (
          state.subTemplateItemIndex <
          state.currentTemplate.templates.length - 1
        ) {
          set({
            subTemplateItemIndex: state.subTemplateItemIndex + 1,
          });
          return;
        }
        break;
      case "quote":
        if (
          state.subTemplateItemIndex <
          state.currentTemplate.rooms.length - 1
        ) {
          set({
            subTemplateItemIndex: state.subTemplateItemIndex + 1,
          });
          return;
        }
        break;
      case "hm_hotel":
      case "hm_offer":
        if (
          state.subTemplateItemIndex <
          state.currentTemplate.galleries.length - 1
        ) {
          set({
            subTemplateItemIndex: state.subTemplateItemIndex + 1,
          });
          return;
        }
        break;
    }

    let currentIndex = state.templates.findIndex(
      (t: any) => t === state.currentTemplate
    );

    if (currentIndex === -1) {
      currentIndex = 0;
    }

    const nextIndex = (currentIndex + 1) % state.templates.length;

    state.setCurrentTemplate(state.templates[nextIndex] ?? null, 0);
    state.setSubTemplateItemIndex(0);
  },
  previous: () => {
    const state = get();

    if (!state.templates || state.templates.length === 0) return;

    switch (state.currentTemplate?.type) {
      case "list_views":
        if (
          state.subTemplateItemIndex >
          state.currentTemplate.templates.length - 1
        ) {
          set({
            subTemplateItemIndex: state.subTemplateItemIndex - 1,
          });
          return;
        }
        break;
      case "quote":
        if (
          state.subTemplateItemIndex >
          state.currentTemplate.rooms.length - 1
        ) {
          set({
            subTemplateItemIndex: state.subTemplateItemIndex - 1,
          });
          return;
        }
        break;
      case "hm_offer":
      case "hm_hotel":
        if (
          state.subTemplateItemIndex >
          state.currentTemplate.galleries.length - 1
        ) {
          set({
            subTemplateItemIndex: state.subTemplateItemIndex - 1,
          });
          return;
        }
        break;
    }

    const currentIndex = state.templates.findIndex(
      (t: any) => t === state.currentTemplate
    );
    const previousIndex =
      (currentIndex - 1 + state.templates.length) % state.templates.length;

    state.setCurrentTemplate(state.templates[previousIndex] ?? null, 0);
    state.setSubTemplateItemIndex(0);
  },
}));

// Stable content key so the same reply's templates are recognized across
// live SSE opens, anchor re-opens and cache reloads (object identity differs).
const contentKey = (template: any): string => {
  const json = JSON.stringify(template);
  let hash = 0;
  for (let i = 0; i < json.length; i++) {
    hash = (hash * 31 + json.charCodeAt(i)) | 0;
  }
  return `${template?.type}-${hash}`;
};

const tagWithKeys = (groupedTemplates: any[]): any[] =>
  groupedTemplates.map((t) => {
    if (!t.__key) t.__key = contentKey({ ...t, __key: undefined });
    return t;
  });

export const groupTemplates = (templates: any[]): any[] => {
  if (!templates?.length) return [];

  const result: any[] = [];
  let run: any[] = [];
  let runType: "list_view" | "map_point" | "video" | null = null;

  const flush = () => {
    if (!run.length) return;
    if (runType === "list_view") {
      result.push({ type: "list_views", templates: run });
    } else if (runType === "map_point") {
      result.push({ type: "map", templates: run });
    } else if (runType === "video") {
      result.push({ type: "videos", templates: run });
    }
    run = [];
    runType = null;
  };

  for (const t of templates) {
    switch (t?.type) {
      case "quote":
      case "hm_offer":
      case "hm_hotel":
      case "poi_map":
      case "itinerary":
        flush();
        result.push(t);
        break;
      case "list_view":
      case "map_point":
      case "video":
        if (runType !== t.type) flush();
        runType = t.type;
        run.push(t);
        break;
    }
  }
  flush();
  return result;
};
